import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import {
  clearQueryHistory,
  deleteQueryHistory,
  listQueryHistory,
  toggleQueryFavorite,
} from "../services/queryHistoryService";

function describeFilters(filters) {
  if (!filters || !filters.length) return "—";
  return filters.map((f) => `${f.field} ${f.op}${f.value !== "" ? ` ${f.value}` : ""}`).join(" AND ");
}

export default function QueryHistoryDialog({ open, onClose, onRunAgain }) {
  const [items, setItems] = useState([]);

  const refresh = () => setItems(listQueryHistory());

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Query History</Typography>
          <Typography variant="caption" color="text.secondary">Last {items.length} queries (favorites kept)</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {items.length === 0 ? (
          <Typography color="text.secondary">No queries recorded yet.</Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Collection</TableCell>
                <TableCell>Filters</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Results</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((q) => (
                <TableRow key={q.id} hover>
                  <TableCell padding="none">
                    <IconButton size="small" onClick={() => { toggleQueryFavorite(q.id); refresh(); }}>
                      {q.favorite ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{q.collection || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {describeFilters(q.filters)}
                      {q.advancedSearch?.term ? ` · search "${q.advancedSearch.term}" (${q.advancedSearch.mode})` : ""}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={q.mode === "server" ? "success" : "default"} label={q.mode} variant="outlined" />
                  </TableCell>
                  <TableCell>{q.resultCount}</TableCell>
                  <TableCell>{q.executionTimeMs} ms</TableCell>
                  <TableCell>{q.createdAt ? new Date(q.createdAt).toLocaleString() : "—"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Run again">
                      <IconButton size="small" color="primary" onClick={() => onRunAgain(q)}>
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => { deleteQueryHistory(q.id); refresh(); }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => { clearQueryHistory(); refresh(); }}>Clear (keep favorites)</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
